import { fetchEventSource } from '@sentool/fetch-event-source'
import { AppStore, keycloak } from 'stylewhere/shared'
import { KEEP_ALIVE_CONTROL_TIME, MAX_RETRY_SSE, RETRY_SSE_TIME } from 'stylewhere/shared/utils'

class ServerSentEvents {
  private static instance: ServerSentEvents

  private controller?: AbortController
  private sseUrl?: string
  private retryTime: number = 0
  private retrySseCount: number = 0
  private isRunning: boolean = false
  private isTabActive: boolean = true
  private visibilityInitialized: boolean = false
  private keepAliveTimer: NodeJS.Timeout | undefined

  listeners: any = {
    open: [],
    message: [],
    error: [],
    keepAlive: []
  }

  static getInstance(): ServerSentEvents {
    if (!ServerSentEvents.instance) {
      ServerSentEvents.instance = new ServerSentEvents()
    }
    return ServerSentEvents.instance
  }

  initialize(sseUrl, retryTime?: number) {
    if (this.isRunning) return

    this.sseUrl = sseUrl
    this.retryTime = retryTime ?? RETRY_SSE_TIME

    this.initVisibilityHandler()

    this.controller = new AbortController()
    this.isRunning = true
    fetchEventSource(sseUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${AppStore.authToken ?? ''}`
      },
      signal: this.controller.signal,
      onopen: async (response) => {
        this.retrySseCount = 0
        this.listeners.open.forEach((cb) => cb(response))
      },
      onmessage: (msg) => {
        let jsonToSend: string
        try {
          const parsed = JSON.parse(msg)
          jsonToSend = JSON.stringify(parsed)
        } catch {
          jsonToSend = JSON.stringify(msg)
        }

        const parsedCheck = JSON.parse(jsonToSend)
        if (parsedCheck.type === 'keep-alive' || msg.event === 'keep-alive') {
          if (this.listeners.keepAlive.length > 0) this.startKeepAliveTimer()
          return
        }
        this.listeners.message.forEach((cb) => cb({ data: JSON.stringify(parsedCheck.data) }))
      },
      onerror: (err) => {
        if (err.name === 'AbortError') {
          return
        }

        if (!this.isTabActive) {
          throw err
        }
        this.retrySseCount++
        if (err.status && err.status === 401) {
          keycloak.logout()
          AppStore.saveAuthToken().then(() => this.retry)
        } else {
          this.retry()
        }
      }
    })
  }

  private startKeepAliveTimer = () => {
    this.clearKeepAliveTimer()
    this.keepAliveTimer = setTimeout(() => {
      this.isRunning = false
      this.listeners.keepAlive.forEach((cb) => cb())
      this.retrySseCount++
      this.retry()
    }, KEEP_ALIVE_CONTROL_TIME)
  }

  private clearKeepAliveTimer = () => {
    if (this.keepAliveTimer) {
      clearTimeout(this.keepAliveTimer)
      this.keepAliveTimer = undefined
    }
  }

  private initVisibilityHandler() {
    if (this.visibilityInitialized) return
    this.visibilityInitialized = true
    document.addEventListener('visibilitychange', () => {
      this.isTabActive = !document.hidden
      if (!this.isTabActive) {
        this.destroy()
      } else {
        if (this.sseUrl) {
          this.retrySseCount = 0
          this.initialize(this.sseUrl)
        }
      }
    })
  }

  private retry = () => {
    this.destroy()
    if (this.retrySseCount === 1) {
      this.initialize(this.sseUrl)
    } else {
      if (this.retrySseCount <= MAX_RETRY_SSE) {
        setTimeout(() => {
          this.initialize(this.sseUrl)
        }, this.retryTime)
      } else {
        this.listeners.error.forEach((cb) => cb())
      }
    }
  }

  addEventListener(type: 'open' | 'message' | 'error' | 'keepAlive', callback: Function) {
    this.listeners[type]?.push(callback)
    return () => {
      this.listeners[type] = this.listeners[type].filter((cb) => cb !== callback)
    }
  }

  destroy(reset: boolean = false) {
    if (reset) this.clearListener()
    if (this.controller) {
      this.controller.abort()
      this.controller = undefined
    }
    this.isRunning = false
  }

  clearListener() {
    this.listeners = {
      open: [],
      message: [],
      error: [],
      keepAlive: []
    }
  }
}

export const RNEventSource = ServerSentEvents.getInstance()
